import '../../../ProductCard/ProductCard.scss';

import classnames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import loadable from '@loadable/component';

import { listerClickThrough } from '../../../../actions/lister-actions';
import { useEventListener } from '../../../../hooks/useEventListener';
import { useIsProductInHighDemand } from '../../../../hooks/useIsProductInHighDemand';
import TRANSLATIONS from '../../../../translations/translations@ade.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
} from '../../../../utilities/lister-utilities';
import { isProductInTradingRange } from '../../../../utilities/product-utilities';
import AddToBasketButton from '../../../ProductCard/AddToBasket/AddToBasketButton@mobile';
import EnergyInformation from '../../../ProductCard/EnergyInformation/EnergyInformation';
import Heading from '../../../ProductCard/Heading';
import HighDemandFlag from '../../../ProductCard/HighDemand/HighDemandFlag';
import HighDemandMessaging from '../../../ProductCard/HighDemand/HighDemandMessaging';
import MoreInfo from '../../../ProductCard/MoreInfo';
import StockStatus from '../../../ProductCard/StockStatus';
import TradingRangeFlag from '../../../ProductCard/TradingRangeFlag/TradingRangeFlag';
import CustomCatBullets from '../../customCategory/CustomCatBullets';
import CustomCatFinanceProposition from '../../customCategory/CustomCatFinanceProposition';
import CustomCatMobileDeliveryBlock from '../../customCategory/CustomCatMobileDeliveryBlock';
import CustomCatPricePod from '../../customCategory/CustomCatPricePod@default';
import CustomCatProductImage from '../../customCategory/CustomCatProductImage';
import PromoFlag from '../../customCategory/CustomCatPromoFlag';
import CustomCatReviewRating from '../../customCategory/CustomCatReviewRating';
import Divider from '../../customCategory/Divider';
import { Context } from '../../store/store';
import MobileProductCardSkeleton from './Skeletons/MobileProductCardSkeleton';

const BackInStockForm = loadable(() =>
	import(
		/* webpackPrefetch: true */ '../../../ProductCard/BackInStockForm/BackInStockForm.js'
	)
);

export const CustomCategoryMobileProductCard = ({ product, observer }) => {
	const card = useRef(null);

	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	const dispatch = useDispatch();

	const isSkuHighDemand = useIsProductInHighDemand(product);

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	useEffect(() => {
		const cardRef = card.current;

		if (observer && cardRef) {
			observer.observe(cardRef);
		}

		return () => {
			if (observer && cardRef) {
				observer.unobserve(cardRef);
			}
		};
	}, [card.current, observer]);

	if (!product) {
		return null;
	}

	const tradingRangeInfo = isProductInTradingRange(product.Code);

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);

	if (!loaded) {
		return <MobileProductCardSkeleton ColourId={product.ColourId} />;
	}

	return (
		<>
			<a
				href={product.FullProductUrl}
				onClick={(event) => {
					event.preventDefault();
					window.location.assign(product.FullProductUrl);

					dispatch(listerClickThrough(product.Code));
				}}
				className="product-card__link"
				itemProp="url"
				data-testid="product-card-link"
				data-tag-name="lister product card"
				data-tag-type={product.IsFeaturedProduct ? 'featured' : 'standard'}
				data-tag-delivery-date={deliveryDate}
				data-tag-delivery-days={deliveryDaysDifference}>
				<span>
					{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
				</span>
			</a>
			{tradingRangeInfo && <TradingRangeFlag {...tradingRangeInfo} small />}
			<PromoFlag {...product} />
			<Heading content={product.FullTitle} />
			<CustomCatReviewRating classes="mt-0" isRecommendedProduct {...product} />
			<CustomCatProductImage {...product} classes="my-4" />
			<StockStatus
				classes="mt-4"
				stockStatus={product.State}
				productCode={product.Code}
				alignCenter
			/>
			{isSkuHighDemand && <HighDemandFlag />}
			<CustomCatBullets content={product.FeatureDescriptions} />
			{product.EnergyRating && (
				<EnergyInformation
					classes="flex items-center mt-4 text-center"
					energyRatingDetails={product.EnergyRatingDetails}
					customCatSmall
				/>
			)}
			<div
				className={classnames('flex items-center relative mt-3 md:mt-0', {
					'justify-end': product.State === 'OutOfStock',
				})}>
				{product.State !== 'OutOfStock' ? (
					<>
						<CustomCatFinanceProposition {...product} />
						<Divider />
					</>
				) : null}
				<CustomCatPricePod
					viewModel={product.PricePodViewModel}
					stockStatus={product.State}
				/>
			</div>
			<div className="z-10 mt-auto">
				{isSkuHighDemand && <HighDemandMessaging classes="my-2" />}
				<div className="flex z-10 py-3">
					<MoreInfo {...product} />
					{!isSkuHighDemand && product.State !== 'OutOfStock' && (
						<AddToBasketButton {...product} />
					)}
				</div>
				{product.State === 'OutOfStock' && (
					<BackInStockForm
						productCode={product.Code}
						productUrl={product.ProductUrl}
						productColourId={product.ColourId}
						productState={product.State}
					/>
				)}
				{!isSkuHighDemand && (
					<CustomCatMobileDeliveryBlock
						deliveryMessage={product.DynamicDeliveryBulletsViewModel[0]}
						stockStatus={product.State}
					/>
				)}
			</div>
		</>
	);
};

export default CustomCategoryMobileProductCard;
